/**
 * TanStack Query hooks for the email-template editor (Phase B2.5).
 *
 * Endpoints:
 *   GET  /super-admin/email-templates                                  — list
 *   GET  /super-admin/email-templates/{key}                            — global
 *   GET  /super-admin/email-templates/{key}/tenants/{slug}             — tenant
 *   PUT  /super-admin/email-templates/{key}                            — save global
 *   PUT  /super-admin/email-templates/{key}/tenants/{slug}             — save tenant
 *   DELETE /super-admin/email-templates/{key}/tenants/{slug}           — drop override
 *   GET  /super-admin/email-templates/{key}/versions                   — history
 *   POST /super-admin/email-templates/{key}/restore/{version}          — restore
 *   POST /super-admin/email-templates/{key}/preview                    — render
 *   POST /super-admin/email-templates/{key}/test-send                  — send test
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  TemplateListResponse,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplatePublic,
  TemplateTestSendResponse,
  TemplateUpdateRequest,
  TemplateVersionListResponse,
} from "./types";

const ROOT = "/super-admin/email-templates";

const baseKey = (key: string, tenantSlug?: string | null) =>
  tenantSlug ? [`${ROOT}/${key}/tenants/${tenantSlug}`] : [`${ROOT}/${key}`];

export function useTemplateList() {
  return useQuery<TemplateListResponse>({
    queryKey: [ROOT],
    queryFn: () => api.get<TemplateListResponse>(ROOT),
  });
}

export function useTemplate(key: string, tenantSlug?: string | null) {
  return useQuery<TemplatePublic>({
    queryKey: baseKey(key, tenantSlug ?? null),
    queryFn: () =>
      api.get<TemplatePublic>(
        tenantSlug ? `${ROOT}/${key}/tenants/${tenantSlug}` : `${ROOT}/${key}`,
      ),
    enabled: Boolean(key),
  });
}

export function useUpdateTemplate(key: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  const path = tenantSlug ? `${ROOT}/${key}/tenants/${tenantSlug}` : `${ROOT}/${key}`;
  return useMutation<TemplatePublic, Error, TemplateUpdateRequest>({
    mutationFn: (body) => api.put<TemplatePublic>(path, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey(key, tenantSlug ?? null) });
      qc.invalidateQueries({ queryKey: [`${ROOT}/${key}/versions`] });
      qc.invalidateQueries({ queryKey: [ROOT] });
    },
  });
}

export function useDeleteTenantOverride(key: string, tenantSlug: string) {
  const qc = useQueryClient();
  const path = `${ROOT}/${key}/tenants/${tenantSlug}`;
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await api.delete(path);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey(key, tenantSlug) });
      qc.invalidateQueries({ queryKey: [ROOT] });
    },
  });
}

export function usePreviewTemplate(
  key: string,
): UseMutationResult<TemplatePreviewResponse, Error, TemplatePreviewRequest> {
  return useMutation<TemplatePreviewResponse, Error, TemplatePreviewRequest>({
    mutationFn: (body) => api.post<TemplatePreviewResponse>(`${ROOT}/${key}/preview`, body),
  });
}

export function useTestSendTemplate(key: string) {
  return useMutation<TemplateTestSendResponse, Error, TemplatePreviewRequest>({
    mutationFn: (body) => api.post<TemplateTestSendResponse>(`${ROOT}/${key}/test-send`, body),
  });
}

export function useTemplateVersions(key: string, tenantSlug?: string | null) {
  const qs = tenantSlug ? `?tenant_slug=${encodeURIComponent(tenantSlug)}` : "";
  return useQuery<TemplateVersionListResponse>({
    queryKey: [`${ROOT}/${key}/versions`, tenantSlug ?? null],
    queryFn: () => api.get<TemplateVersionListResponse>(`${ROOT}/${key}/versions${qs}`),
  });
}

export function useRestoreVersion(key: string, tenantSlug?: string | null) {
  const qc = useQueryClient();
  return useMutation<TemplatePublic, Error, { version: number; notes?: string }>({
    mutationFn: ({ version, notes }) =>
      api.post<TemplatePublic>(
        `${ROOT}/${key}/restore/${version}${
          tenantSlug ? `?tenant_slug=${encodeURIComponent(tenantSlug)}` : ""
        }`,
        { notes: notes ?? null },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey(key, tenantSlug ?? null) });
      qc.invalidateQueries({ queryKey: [`${ROOT}/${key}/versions`] });
    },
  });
}
